<template>
    <div class="content-container">
        <div class="filter-container">
            <Filter
                :filter="this.filter"
                :statusList="this.statusList"
                @addFilterStatus="this.addFilterStatus"
                @clearFilter="this.clearFilter"
                @removeFilterStatus="this.removeFilterStatus"
                @search="search"
                @setDevisId="this.setDevisId"
                @setDevisPubliDateFrom="this.setDevisPubliDateFrom"
                @setDevisPubliDateTo="this.setDevisPubliDateTo"
                @setNumFactures="this.setNumFactures"
                @setNumero="this.setNumero"
                @statusToggleSelectAll="this.statusToggleSelectAll"
            />
        </div>
        <div class="dataTable">
            <div style="margin-bottom: 8px">
                <h2>
                    {{ this.count }}
                    {{ $t('devis.dataTable.title', this.count > 1 ? 2 : 1) }}
                </h2>
                <BorderedIconButton
                    v-if="!isExportDisabled"
                    :imgUrl="iconeXls"
                    :title="$t('list.export')"
                    @click="exportXls"
                />
            </div>
            <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
            <div v-else ref="content" class="datatable-container" @scroll="handleScroll">
                <DataTable
                    :count="+count"
                    :isLoading="isLoading"
                    :tableData="this.tableData"
                    :tableHeader="this.tableHeader"
                    @select="goToPage"
                    @sort="doSort"
                />
            </div>
        </div>
        <WaitingModal v-if="isExportXlsLoading" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import iconeXls from '@/assets/img/xls.svg';
import { store } from '@/plugins/store';
import { Devis, EtatDevis } from '@/models/Devis';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import currencyFormatter from 'currency-formatter';
import { SortType, sortValue } from '@/models/SortType';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import router from '@/router';
import { FilterDevis } from '@/modules/Devis/services/DevisService';
import { DevisActions } from '@/modules/Devis/store/action-types';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import Filter from '@/modules/Common/components/Filter.vue';

export default defineComponent({
    name: 'historiqueDevisTab',
    components: { Filter, DataTable, LoaderComponent, BorderedIconButton, WaitingModal },

    props: {
        phaseId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            filter: {
                statusList: Object.values(EtatDevis) as string[],
                devisId: '',
                numero: '',
                numFactures: '',
            },
        };
    },
    setup() {
        const content = ref<HTMLElement | null>(null);
        return {
            content,
            iconeXls,
        };
    },
    beforeMount() {
        this.filter.statusList = [...this.filterStatus];
        this.filter.devisId = this.filterDevisId;
        this.filter.numero = this.filterNumero;
        this.filter.numFactures = this.filterNumFactures;
    },
    computed: {
        historiqueDevisState() {
            return store.state.equipementState.historiqueDevis;
        },
        tableData() {
            return store.getters['equipementState/getHistoriqueDevis'].map(
                ({
                    numero,
                    numCommande,
                    version,
                    objet,
                    prix,
                    monnaie,
                    montantFacture,
                    numFactures,
                    etatDevis,
                    chiffrageId,
                    dateChiffrage,
                    demandeDevisId,
                }: Devis) => ({
                    id: { pageName: 'detailDevisPage', value: chiffrageId, label: numero + '-' + version },
                    objet,
                    numero: numCommande,
                    demandeDevisId: demandeDevisId != null ? demandeDevisId : '',
                    prix: currencyFormatter.format(prix, { code: monnaie }),
                    montantFacture: montantFacture ? currencyFormatter.format(montantFacture, { code: monnaie }) : '',
                    numFactures:
                        numFactures?.reduce((list, item, index) => {
                            return `${list}${index === 0 ? '' : ' '}${item}`;
                        }, '') ?? '',
                    dateChiffrage: new Date(dateChiffrage).toLocaleDateString(),
                    statut: statusName(etatDevis),
                })
            );
        },

        tableHeader() {
            return store.getters['equipementState/getHistoriqueDevisTableHeader'];
        },
        count() {
            return store.getters['equipementState/getHistoriqueDevisCount'];
        },
        isLoading() {
            return store.getters['equipementState/getHistoriqueDevisIsLoading'];
        },
        page() {
            return store.getters['equipementState/getHistoriqueDevisPage'];
        },
        numberOfPages() {
            return store.getters['equipementState/getHistoriqueDevisNbOfPages'];
        },
        filterStatus(): string[] {
            return store.getters['equipementState/getHistoriqueDevisFilter'].filterStatus;
        },
        filterDevisId(): string {
            return store.getters['equipementState/getHistoriqueDevisFilter'].filterDevisId;
        },
        filterNumFactures(): string {
            return store.getters['equipementState/getHistoriqueDevisFilter'].filterNumFactures;
        },
        filterNumero(): string {
            return store.getters['equipementState/getHistoriqueDevisFilter'].filterNumero;
        },
        statusList() {
            return Object.values(EtatDevis);
        },
        allStatusSelected() {
            return this.filter.statusList.length === this.statusList.length || this.filter.statusList.length === 0;
        },
        isExportDisabled() {
            return this.tableData.length === 0 || this.isLoading;
        },
        isExportXlsLoading() {
            return store.getters['equipementState/getHistoriqueDevisIsExportXlsLoading'];
        },
    },
    mounted() {
        this.getHistoriqueDevisData();
    },
    methods: {
        getHistoriqueDevisData(page = 0) {
            const storeFilter = store.getters['equipementState/getHistoriqueDevisFilter'];
            const filters: FilterDevis = {
                page,
                numFacture: storeFilter.filterNumFactures !== '' ? storeFilter.filterNumFactures : undefined,
                numCommande: storeFilter.filterNumero !== '' ? storeFilter.filterNumero : undefined,
                numDevis: storeFilter.filterDevisId !== '' ? storeFilter.filterDevisId : undefined,
                etatDevis: this.allStatusSelected ? [] : storeFilter.filterStatus,
                phasesIds: [this.phaseId],
                sort: this.historiqueDevisState.sort
                    ? sortValue(this.historiqueDevisState.sort.sortData, this.historiqueDevisState.sort.sortStatus)
                    : undefined,
                isBim: true,
            };

            store.dispatch(`${DevisActions.GET_DEVIS}`, filters, {
                root: true,
            });
        },
        search() {
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_FILTER_DEVIS_ID}`, this.filter.devisId);
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_FILTER_STATUS}`, this.filter.statusList);

            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_FILTER_DEVIS_NUM_FACTURE}`, this.filter.numFactures);
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_FILTER_DEVIS_NUMERO_COMMANDE}`, this.filter.numero);

            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_ITEMS}`, []);
            this.getHistoriqueDevisData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_SORT}`, sort);
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_SET_ITEMS}`, []);
            this.getHistoriqueDevisData();
        },
        setDevisId(value: string) {
            this.filter.devisId = value;
        },
        setNumero(value: string) {
            this.filter.numero = value;
        },
        setNumFactures(value: string) {
            this.filter.numFactures = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(s => s !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList =
                this.filter.statusList.length === this.statusList.length ? [] : [...this.statusList];
        },
        clearFilter() {
            store.commit(`equipementState/${EQUIPEMENT.DEVIS_RESET_FILTER}`);
            this.filter.devisId = '';
            this.filter.numero = '';
            this.filter.numFactures = '';
            this.filter.statusList = [...this.statusList];

            this.search();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getHistoriqueDevisData(this.page + 1);
            }
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        exportXls() {
            const storeFilter = store.getters['equipementState/getHistoriqueDevisFilter'];
            const filters: FilterDevis = {
                page: 0,
                numFacture: storeFilter.filterNumFactures !== '' ? storeFilter.filterNumFactures : undefined,
                numCommande: storeFilter.filterNumero !== '' ? storeFilter.filterNumero : undefined,
                numDevis: storeFilter.filterDevisId !== '' ? storeFilter.filterDevisId : undefined,
                etatDevis: this.allStatusSelected ? [] : storeFilter.filterStatus,
                phasesIds: [this.phaseId],
                sort: this.historiqueDevisState.sort
                    ? sortValue(this.historiqueDevisState.sort.sortData, this.historiqueDevisState.sort.sortStatus)
                    : undefined,
                isBim: true,
            };

            store.dispatch(`${DevisActions.EXPORT_XLS}`, filters, {
                root: true,
            });
        },
    },
});
</script>
<style scoped>
.dataTable > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
